import { zodResolver } from "@hookform/resolvers/zod";
import { Button, FormControl, InputAdornment, MenuItem, Stack, Typography } from "@mui/material";
import * as api from "api";
import { CustomInputLabel } from "component/CustomInputLabel";
import { ErrorAlert } from "component/ErrorAlert";
import { ErrorText } from "component/ErrorMessage";
import { FormDateTimePicker } from "component/FormDateTimePicker";
import { FormRadioGroup } from "component/FormRadioGroup";
import { FormSelect } from "component/FormSelect";
import { AlignJustifyIcon, ChevronDownIcon, Dashboard3Icon, DoubleArrowUpIcon } from "component/Icons";
import {
  DEFAULT_TIME_ZONE,
  PLANNED_DEPARTURE_OPTIONS,
  PRIORITY_SELECT_ITEMS,
  RISK_SCORE_SELECT_ITEMS,
  SPECIAL_CONDITION_SELECT_ITEMS,
} from "constant";
import { format } from "date-fns-tz";
import { enAU } from "date-fns/locale";
import { Controller, useForm } from "react-hook-form";
import {
  ActionFunction,
  LoaderFunction,
  redirect,
  useFetcher,
  useLoaderData,
  useRouteLoaderData,
} from "react-router-dom";
import { COLORS } from "style";
import {
  Hospital,
  Mission,
  MissionAssessmentConditionEnum,
  MissionPlannedDepartureEnum,
  MissionStatusEnum,
  MissionTaskingTypeEnum,
  User,
} from "type/model/api";
import { UserRoles } from "type/model/view";
import { CreateNetsMissionFormProps, createNetsMissionSchema } from "type/schema";
import { getApiErrorMessage } from "utility/error-message";
import { checkRoleLoader, pathWithSearchParams } from "utility/router";
import { CustomSelect } from "../../component/CustomSelect";

export const createNetsMissionLoader: LoaderFunction = async (params) => {
  await checkRoleLoader([UserRoles.Tasker])(params);

  let hospitals: Hospital[] = [];
  try {
    const hospitalsQuery = await api.queryClient.ensureQueryData({
      queryKey: ["hospitals"],
      queryFn: api.fetchHospitals,
    });
    hospitals = hospitalsQuery.data;
  } catch {}

  return { hospitals };
};

export const createNetsMissionAction: ActionFunction = async ({ request }) => {
  const formValues = await request.formData();
  const missionValues = formValues.get("mission");
  if (missionValues && typeof missionValues === "string") {
    try {
      const response = await api.createMission({ mission: JSON.parse(missionValues) });
      const missionId = response.data.data.id;
      return redirect(pathWithSearchParams(`/main/dashboard/mission/${missionId}/recommendations`));
    } catch (error) {
      return { formError: getApiErrorMessage(error) };
    }
  } else {
    return { formError: "Invalid form data format!" };
  }
};

export const CreateNetsMission = () => {
  const { hospitals } = useLoaderData() as { hospitals: Hospital[] };
  const { currentUser } = useRouteLoaderData("main") as { currentUser: User };
  const currentUserTimeZone = currentUser.time_zone || DEFAULT_TIME_ZONE;
  const currentUserTimeZoneAbbreviation = format(Date.now(), "zzz", {
    timeZone: currentUserTimeZone,
    locale: enAU,
  });

  const fetcher = useFetcher();
  const { handleSubmit, control, watch } = useForm({
    resolver: zodResolver(createNetsMissionSchema),
    defaultValues: {
      isDropOffOnly: "false",
      referringHospital: hospitals[0]?.id,
      destinationHospital: hospitals[0]?.id,
      plannedDeparture: MissionPlannedDepartureEnum.Now,
      specialCondition: MissionAssessmentConditionEnum.TotalMedicalTime,
    },
    mode: "all",
  });

  const plannedDepartureValue = watch("plannedDeparture");

  const onSubmit = async (values: Partial<CreateNetsMissionFormProps>) => {
    const formData = new FormData();

    const transformedData: Partial<Mission> = {
      status: MissionStatusEnum.Draft,
      is_multipatient: false,
      aviation_risk: values.riskScore,
      tasking_type: MissionTaskingTypeEnum.SecondaryNets,
      planned_departure: values.plannedDeparture,
      planned_departure_at:
        values.departureTime && values.plannedDeparture !== "now"
          ? values.departureTime.toISOString()
          : values.plannedDeparture,
      assessment_condition: values.specialCondition,
      is_drop_off_only: values.isDropOffOnly === "true",
      // @ts-ignore - needs to be updated in swagger gen types
      tasks_attributes: [
        {
          position: 100,
          task: "patient_transport",
          from_hospital_id: values.referringHospital,
          to_hospital_id: values.destinationHospital,
          patient_priority: values.priority,
        },
      ],
    };

    formData.append("mission", JSON.stringify(transformedData));

    fetcher.submit(formData, {
      action: "/main/dashboard/mission/create/nets",
      method: "post",
    });
  };

  return (
    <Stack component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ gap: 2 }}>
      <Stack
        sx={{
          flexGrow: 1,
          p: 4,
          border: `2px solid ${COLORS.WHITE_SMOKE}`,
          width: "100%",
          borderRadius: "20px",
          borderTop: 0,
          borderStartStartRadius: 0,
          borderStartEndRadius: 0,
        }}
      >
        <Stack sx={{ width: "100%", maxWidth: 720, marginLeft: "10%", gap: 4, alignItems: "flex-start" }}>
          <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between" }}>
            <Controller
              name="priority"
              control={control}
              render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                <FormSelect
                  label="Priority"
                  required
                  placeholder="Select a priority"
                  displayEmpty={true}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={error?.message}
                  sx={{ minWidth: 350 }}
                  startAdornment={
                    <InputAdornment position="start">
                      <DoubleArrowUpIcon />
                    </InputAdornment>
                  }
                  items={PRIORITY_SELECT_ITEMS}
                />
              )}
            />
            <Controller
              name="riskScore"
              control={control}
              render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                <FormSelect
                  label="Risk score"
                  placeholder="Select a risk score"
                  displayEmpty={true}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={error?.message}
                  sx={{ minWidth: 350 }}
                  startAdornment={
                    <InputAdornment position="start">
                      <Dashboard3Icon />
                    </InputAdornment>
                  }
                  items={RISK_SCORE_SELECT_ITEMS}
                />
              )}
            />
          </Stack>
          <Controller
            name="specialCondition"
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormSelect
                label="Special conditions"
                placeholder="Select a mission condition"
                displayEmpty={true}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                sx={{ minWidth: 350 }}
                startAdornment={
                  <InputAdornment position="start">
                    <AlignJustifyIcon />
                  </InputAdornment>
                }
                items={SPECIAL_CONDITION_SELECT_ITEMS}
              />
            )}
          />
          <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between" }}>
            <Controller
              name="referringHospital"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl>
                  <CustomInputLabel sx={{ marginBottom: "8px" }} required>
                    Referring Hospital
                  </CustomInputLabel>
                  <CustomSelect
                    displayEmpty={true}
                    renderValue={(value) => {
                      const hospital = hospitals.find((hospital) => hospital.id === value);
                      return hospital ? hospital.name : "Select a hospital";
                    }}
                    {...field}
                    size="small"
                    sx={{ minWidth: 350 }}
                    variant="outlined"
                    IconComponent={ChevronDownIcon}
                    MenuProps={{
                      disableAutoFocusItem: true,
                      MenuListProps: { sx: { bgcolor: COLORS.DR_WHITE } },
                    }}
                  >
                    {hospitals.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                  {error ? (
                    <Stack sx={{ mt: 1, maxWidth: 350 }}>
                      <ErrorText>{error.message}</ErrorText>
                    </Stack>
                  ) : null}
                </FormControl>
              )}
            />
          </Stack>
          <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between" }}>
            <Controller
              name="destinationHospital"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl>
                  <CustomInputLabel sx={{ marginBottom: "8px" }} required>
                    Destination Hospital
                  </CustomInputLabel>
                  <CustomSelect
                    displayEmpty={true}
                    renderValue={(value) => {
                      const hospital = hospitals.find((hospital) => hospital.id === value);
                      return hospital ? hospital.name : "Select a hospital";
                    }}
                    {...field}
                    size="small"
                    sx={{ minWidth: 350 }}
                    variant="outlined"
                    IconComponent={ChevronDownIcon}
                    MenuProps={{
                      disableAutoFocusItem: true,
                      MenuListProps: { sx: { bgcolor: COLORS.DR_WHITE } },
                    }}
                  >
                    {hospitals.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                  {error ? (
                    <Stack sx={{ mt: 1, maxWidth: 350 }}>
                      <ErrorText>{error.message}</ErrorText>
                    </Stack>
                  ) : null}
                </FormControl>
              )}
            />
          </Stack>
          <Stack sx={{ gap: 1 }}>
            <Typography variant="h2">Logistics</Typography>
            <Typography>Select the relevant mission times</Typography>
          </Stack>
          <Controller
            name="plannedDeparture"
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormRadioGroup
                fullWidth
                row
                label="Planned departure"
                required
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                items={PLANNED_DEPARTURE_OPTIONS}
              />
            )}
          />
          {plannedDepartureValue === MissionPlannedDepartureEnum.SpecificTime ? (
            <Controller
              name="departureTime"
              control={control}
              render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
                <FormDateTimePicker
                  icon="clock"
                  label={`Departure time (${currentUserTimeZoneAbbreviation})`}
                  required
                  placeholder="Enter the departing time"
                  sx={{ width: 350 }}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  error={error?.message}
                />
              )}
            />
          ) : null}
          <Controller
            name="isDropOffOnly"
            control={control}
            render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
              <FormRadioGroup
                fullWidth
                row
                label="Drop off only"
                required
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={error?.message}
                items={[
                  { label: "Yes", value: "true" },
                  { label: "No", value: "false" },
                ]}
              />
            )}
          />
        </Stack>
      </Stack>

      <Stack direction="row" sx={{ alignItems: "flex-start", justifyContent: "flex-end", gap: 4 }}>
        {fetcher.data?.formError ? <ErrorAlert>{fetcher.data.formError}</ErrorAlert> : null}
        <Button
          type="submit"
          loading={fetcher.state === "loading" || fetcher.state === "submitting"}
          variant="contained"
          sx={{ whiteSpace: "nowrap" }}
        >
          Next
        </Button>
      </Stack>
    </Stack>
  );
};
